import { useEffect, useState } from 'react';
import { ecorpResources } from '@command-center/admin-ecorp';
import { Card, CardContent, usePageHeader } from '@command-center/ui-system';
import { Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { listRecords, type PortalRecord } from '../api';
import { EmptyState, ErrorNotice } from '../lib';
import { documentNeedsApproval, RecordTable } from '../ResourceView';
import { DocumentDetail } from '../components/DocumentDetail';

export async function fetchPendingApprovalDocuments(): Promise<PortalRecord[]> {
  const page = await listRecords(
    'documents',
    new URLSearchParams({ pageSize: '100', sortField: 'Uploaded At', sortDirection: 'desc' }),
  );
  return page.items.filter(documentNeedsApproval);
}

export function ApprovalsView({
  onContext,
  onCountChange,
}: {
  onContext(value: Record<string, unknown>): void;
  onCountChange(count: number): void;
}) {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState<PortalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const documentId = params.get('open') ?? undefined;
  usePageHeader(
    {
      title: 'Approvals',
      subtitle: 'Review extracted document information before it updates a project.',
      context:
        items.length > 0 ? (
          <span>
            {items.length} document{items.length === 1 ? '' : 's'} waiting
          </span>
        ) : undefined,
    },
    [items.length],
  );

  async function load() {
    setLoading(true);
    setError(undefined);
    try {
      const pending = await fetchPendingApprovalDocuments();
      setItems(pending);
      onCountChange(pending.length);
      onContext({ resource: 'approvals', pendingApprovals: pending.length });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load approvals.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);
  function open(item: PortalRecord) {
    setParams({ open: item.id });
    onContext({
      resource: 'approvals',
      documentId: item.id,
      documentName: String(item.fields['Document Name'] ?? ''),
    });
  }
  function close() {
    params.delete('open');
    setParams(params, { replace: true });
  }

  return (
    <section className="space-y-4">
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">
          {items.length} document{items.length === 1 ? '' : 's'} currently need review or approval.
        </CardContent>
      </Card>
      <div className="contents">
        {error && <ErrorNotice message={error} onRetry={() => void load()} />}
        {loading ? (
          <div className="flex items-center gap-2 p-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading approvals…
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title="You’re all caught up."
            hint="Documents that need review or approval will appear here."
          />
        ) : (
          <RecordTable items={items} resource={ecorpResources.documents} onSelect={open} />
        )}
      </div>
      <DocumentDetail
        documentId={documentId}
        open={Boolean(documentId)}
        reviewMode
        onClose={close}
        onChanged={() => void load()}
      />
    </section>
  );
}
